import { computed, ref, type Ref } from "vue";

const RECENT_LIMIT = 8;
// Both history endpoints retain and accept at most 50 entries per user.
const HISTORY_LIMIT = 50;
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const STALE_MS = 15_000;

export function useRecentSearchHistory<T extends { searchedAt: string }>(
	entries: Ref<T[]>,
	fetchEntries: (limit: number) => Promise<T[]>,
	onError: (error: unknown, expanding: boolean) => void,
) {
	const loading = ref(false);
	const loadedAt = ref(0);
	const pastThreeDays = ref(false);
	const windowEnd = ref(Date.now());
	const visibleEntries = computed(() => {
		if (!pastThreeDays.value) return entries.value.slice(0, RECENT_LIMIT);
		const cutoff = windowEnd.value - THREE_DAYS_MS;
		return entries.value.filter((entry) => {
			const timestamp = Date.parse(entry.searchedAt);
			return timestamp >= cutoff && timestamp <= windowEnd.value;
		});
	});

	async function load(force = false, expand = pastThreeDays.value) {
		if (loading.value) return;
		if (!force && Date.now() - loadedAt.value < STALE_MS) return;
		loading.value = true;
		try {
			const nextEntries = await fetchEntries(expand ? HISTORY_LIMIT : RECENT_LIMIT);
			entries.value = nextEntries;
			windowEnd.value = Date.now();
			pastThreeDays.value = expand;
			loadedAt.value = windowEnd.value;
		} catch (error) {
			// Preserve the current list and range so a failed expansion can be retried.
			onError(error, expand);
		} finally {
			loading.value = false;
		}
	}

	async function changeRange() {
		if (loading.value) return;
		if (pastThreeDays.value) {
			pastThreeDays.value = false;
			loadedAt.value = 0;
			return;
		}
		await load(true, true);
	}

	return { loading, loadedAt, pastThreeDays, visibleEntries, load, changeRange };
}
